import { del as idbDel, get as idbGet, set as idbSet } from 'idb-keyval';
import { create } from 'zustand';
import type { PersistStorage, StorageValue } from 'zustand/middleware';
import { devtools, persist } from 'zustand/middleware';

const THUMBNAIL_TTL_MS = 24 * 60 * 60 * 1000;

const MAX_THUMBNAIL_ENTRIES = 30;

const THUMBNAIL_CACHE_STORAGE_KEY = 'easyclaim-thumbnail-cache';

interface ThumbnailCacheEntry {
	blob: Blob;
	expiresAt: number;
}

interface PersistedThumbnailState {
	entriesByImageId: Record<string, ThumbnailCacheEntry>;
}

interface PhotoThumbnailState extends PersistedThumbnailState {
	thumbnailsByImageId: Record<string, string>;
	setThumbnail: (imageId: string, blob: Blob) => void;
	clearThumbnail: (imageId: string) => void;
	clearAll: () => void;
}

interface PhotoThumbnailHydrationState {
	hasHydrated: boolean;
}

export const usePhotoThumbnailHydrationStore = create<PhotoThumbnailHydrationState>()(() => ({
	hasHydrated: false,
}));

function revokeIfObjectUrl(url: string | undefined) {
	if (url?.startsWith('blob:')) {
		URL.revokeObjectURL(url);
	}
}

function isExpired(entry: ThumbnailCacheEntry | undefined): boolean {
	return !entry || entry.expiresAt <= Date.now();
}

function pruneEntries(
	entriesByImageId: Record<string, ThumbnailCacheEntry>,
	thumbnailsByImageId: Record<string, string>
): {
	entriesByImageId: Record<string, ThumbnailCacheEntry>;
	thumbnailsByImageId: Record<string, string>;
} {
	const nextEntries = { ...entriesByImageId };
	const nextThumbnails = { ...thumbnailsByImageId };

	for (const [imageId, entry] of Object.entries(nextEntries)) {
		if (isExpired(entry)) {
			revokeIfObjectUrl(nextThumbnails[imageId]);
			delete nextEntries[imageId];
			delete nextThumbnails[imageId];
		}
	}

	const overflow = Object.keys(nextEntries).length - MAX_THUMBNAIL_ENTRIES;

	if (overflow > 0) {
		const oldestFirst = Object.entries(nextEntries).sort(
			([, a], [, b]) => a.expiresAt - b.expiresAt
		);

		for (let i = 0; i < overflow; i++) {
			const [imageId] = oldestFirst[i];

			revokeIfObjectUrl(nextThumbnails[imageId]);
			delete nextEntries[imageId];
			delete nextThumbnails[imageId];
		}
	}

	return { entriesByImageId: nextEntries, thumbnailsByImageId: nextThumbnails };
}

const idbPersistStorage: PersistStorage<PersistedThumbnailState> = {
	getItem: async (name) => {
		const value = await idbGet<StorageValue<PersistedThumbnailState>>(name);

		return value ?? null;
	},
	setItem: (name, value) => idbSet(name, value),
	removeItem: (name) => idbDel(name),
};

export const usePhotoThumbnailStore = create<PhotoThumbnailState>()(
	devtools(
		persist(
			(set, get) => ({
				entriesByImageId: {},
				thumbnailsByImageId: {},
				setThumbnail: (imageId, blob) => {
					revokeIfObjectUrl(get().thumbnailsByImageId[imageId]);
					const objectUrl = URL.createObjectURL(blob);

					set(
						(state) => {
							const entriesByImageId = {
								...state.entriesByImageId,
								[imageId]: { blob, expiresAt: Date.now() + THUMBNAIL_TTL_MS },
							};
							const thumbnailsByImageId = { ...state.thumbnailsByImageId, [imageId]: objectUrl };

							return pruneEntries(entriesByImageId, thumbnailsByImageId);
						},
						undefined,
						'setThumbnail'
					);
				},
				clearThumbnail: (imageId) => {
					revokeIfObjectUrl(get().thumbnailsByImageId[imageId]);
					set(
						(state) => {
							const entriesByImageId = { ...state.entriesByImageId };
							const thumbnailsByImageId = { ...state.thumbnailsByImageId };

							delete entriesByImageId[imageId];
							delete thumbnailsByImageId[imageId];

							return { entriesByImageId, thumbnailsByImageId };
						},
						undefined,
						'clearThumbnail'
					);
				},
				clearAll: () => {
					const { thumbnailsByImageId } = get();

					Object.values(thumbnailsByImageId).forEach(revokeIfObjectUrl);
					set({ entriesByImageId: {}, thumbnailsByImageId: {} }, undefined, 'clearAll');
				},
			}),
			{
				name: THUMBNAIL_CACHE_STORAGE_KEY,
				storage: idbPersistStorage,
				partialize: (state) => ({ entriesByImageId: state.entriesByImageId }),
				onRehydrateStorage: () => (state, error) => {
					if (error || !state) {
						usePhotoThumbnailHydrationStore.setState({ hasHydrated: true });
						return;
					}
					const rehydratedThumbnails: Record<string, string> = {};

					for (const [imageId, entry] of Object.entries(state.entriesByImageId)) {
						if (isExpired(entry)) {
							continue;
						}
						rehydratedThumbnails[imageId] = URL.createObjectURL(entry.blob);
					}
					const { entriesByImageId, thumbnailsByImageId } = pruneEntries(
						state.entriesByImageId,
						rehydratedThumbnails
					);

					usePhotoThumbnailStore.setState({
						entriesByImageId,
						thumbnailsByImageId,
					});
					usePhotoThumbnailHydrationStore.setState({ hasHydrated: true });
				},
			}
		),
		{ name: 'PhotoThumbnailStore' }
	)
);

export const useThumbnailFromCache = (imageId: string | undefined) =>
	usePhotoThumbnailStore((state) =>
		imageId && !isExpired(state.entriesByImageId[imageId])
			? state.thumbnailsByImageId[imageId]
			: undefined
	);

export const useHasThumbnailCacheHydrated = () =>
	usePhotoThumbnailHydrationStore((state) => state.hasHydrated);
